import { DeleteOutlined, EditOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Divider } from 'antd';
import React, { useState } from 'react';

import BaseTable from '@components/BaseTable';
import BasicModal from '@components/BasicModal';
import ElementWithPermission from '@components/ElementWithPermission';
import ListPage from '@components/ListPage/ListPage';
import Loading from '@components/Loading/Loading';
import PageWrapper from '@components/PageWrapper/PageWrapper';
import {
    AppConstants,
    DEFAULT_TABLE_ITEM_SIZE,
    DEFAULT_TIME_FORMAT_DISPLAY,
    STATUS_ACTIVE,
    UserTypes,
} from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import { showErrorMessage, showSuccessMessage } from '@services/notifyService';
import { convertUtcToLocalTime } from '@utils/datetimeHelper';
import { getCreatePermissions, getDeletePermissions, getEditPermissions } from '@utils/permission';
import AdminForm from './AdminForm';

// Port từ ra-log-monitor-cms/source/src/containers/users/UserAdminListPage.js -> hook.
// Tạo/sửa bằng MODAL ngay trên trang danh sách.
//
// KHÁC license: cột có thêm Số điện thoại / E-mail / Ngày tạo, KHÔNG có cột 'Quyền';
// cột Hành động LUÔN dựng (nguồn không có checkRenderActionColumn).

const OBJECT_NAME = 'quản trị viên'; // i18n userAdminListPage:objectName
const PAGE_PATH = '/admins';

const UserAdminListPage = () => {
    const [visible, setVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [dataDetail, setDataDetail] = useState({});
    const [loadingSave, setLoadingSave] = useState(false);

    const { execute: executeGetById, loading: loadingGetDetail } = useFetch(apiConfig.user.getById, {
        immediate: false,
    });
    const { execute: executeCreate } = useFetch(apiConfig.user.createAdmin, { immediate: false });
    const { execute: executeUpdate } = useFetch(apiConfig.user.updateAdmin, { immediate: false });
    const { execute: executeUploadFile } = useFetch(apiConfig.file.upload, { immediate: false });

    const { data, mixinFuncs, queryFilter, loading, pagination, deleteLoading } = useListBase({
        apiConfig: {
            getList: apiConfig.user.getList,
            delete: apiConfig.user.delete,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE, // nguồn: 50
            objectName: OBJECT_NAME,
        },
        override: (funcs) => {
            // nguồn saga getUserAdminList: LUÔN gửi kèm kind = UserTypes.ADMIN
            funcs.getList = () => {
                const params = mixinFuncs.prepareGetListParams(queryFilter);
                mixinFuncs.handleFetchList({ ...params, kind: UserTypes.ADMIN });
            };
            // nguồn ListBasePage.onDeleteCompleted đọc `dataList.content` nhưng backend trả `data`
            // -> nhánh "lùi trang khi xoá bản ghi cuối" không bao giờ chạy, luôn getList().
            funcs.onDeleteItemCompleted = () => {
                mixinFuncs.getList();
            };
        },
    });

    // nguồn getDataDetailMapping (giữ nguyên trường groupId thừa)
    const getDataDetailMapping = (raw) => ({
        ...raw,
        groupId: raw?.group && raw.group.id,
    });

    const onShowCreate = () => {
        setIsEditing(false);
        setDataDetail({});
        setVisible(true);
    };

    const onShowEdit = (id) => {
        executeGetById({
            pathParams: { id },
            onCompleted: (res) => {
                setDataDetail(getDataDetailMapping(res?.data));
                setIsEditing(true);
                setVisible(true);
            },
            onError: (err) => {
                if (err?.message) showErrorMessage(err.message);
                else showErrorMessage(` Cập nhật ${OBJECT_NAME} thất bại. Vui lòng thử lại!`);
            },
        });
    };

    // nguồn prepareCreateData: kind + avatarPath ĐẶT TRƯỚC ...values
    const prepareCreateData = (values) => ({
        kind: UserTypes.ADMIN,
        avatarPath: values.avatar,
        ...values,
    });

    // ⚠️ nguồn prepareUpdateData KHÁC license: thêm kind, avatarPath và status, tất cả đặt TRƯỚC ...values
    const prepareUpdateData = (values) => ({
        id: dataDetail.id,
        kind: UserTypes.ADMIN,
        avatarPath: values.avatar,
        status: STATUS_ACTIVE,
        ...values,
    });

    const onOkModal = (values) => {
        setLoadingSave(true);
        const actionName = isEditing ? 'Cập nhật' : 'Tạo mới';
        const execute = isEditing ? executeUpdate : executeCreate;
        execute({
            data: isEditing ? prepareUpdateData(values) : prepareCreateData(values),
            onCompleted: () => {
                setVisible(false);
                setLoadingSave(false);
                mixinFuncs.getList();
                showSuccessMessage(` ${actionName} ${OBJECT_NAME} thành công!`);
            },
            onError: (err) => {
                setLoadingSave(false);
                if (err?.message) showErrorMessage(err.message);
                else showErrorMessage(` ${actionName} ${OBJECT_NAME} thất bại. Vui lòng thử lại!`);
            },
        });
    };

    const uploadFile = ({ params, onCompleted, onError }) => {
        executeUploadFile({ data: params, onCompleted, onError });
    };

    const columns = [
        mixinFuncs.renderIdColumn({ align: 'center' }),
        {
            title: '#',
            dataIndex: 'avatar',
            align: 'center', // nguồn: KHÔNG đặt width cho cột này
            render: (avatar) => (
                <Avatar
                    size="large"
                    icon={<UserOutlined />}
                    src={avatar ? `${AppConstants.contentRootUrl}${avatar}` : null}
                />
            ),
        },
        { title: 'Tên đăng nhập', dataIndex: 'username' },
        { title: 'Họ và tên', dataIndex: 'fullName' },
        { title: 'Số điện thoại', dataIndex: 'phone' },
        { title: 'E-mail', dataIndex: 'email', width: '200px' },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdDate',
            render: (createdDate) => convertUtcToLocalTime(createdDate, DEFAULT_TIME_FORMAT_DISPLAY),
        },
        mixinFuncs.renderStatusColumn({ width: 104 }),
        {
            // nguồn override renderActionColumn: LUÔN dựng cột (không có checkRenderActionColumn)
            title: 'Hành động',
            width: '100px',
            align: 'center',
            render: (dataRow) => (
                <span>
                    <span>
                        <ElementWithPermission permissions={getEditPermissions(PAGE_PATH)}>
                            <Button
                                type="link"
                                className="no-padding"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onShowEdit(dataRow.id);
                                }}
                            >
                                <EditOutlined />
                            </Button>
                        </ElementWithPermission>
                    </span>
                    {/* nguồn: nút Xoá ẩn với tài khoản super admin */}
                    {!dataRow.isSuperAdmin && (
                        <span>
                            <Divider type="vertical" />
                            <ElementWithPermission permissions={getDeletePermissions(PAGE_PATH)}>
                                <Button
                                    type="link"
                                    className="no-padding"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        mixinFuncs.showDeleteItemConfirm(dataRow.id);
                                    }}
                                >
                                    <DeleteOutlined />
                                </Button>
                            </ElementWithPermission>
                        </span>
                    )}
                </span>
            ),
        },
    ];

    const searchFields = [
        { key: 'username', placeholder: 'Tài khoản đăng nhập' },
        { key: 'fullName', placeholder: 'Họ và tên' },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: 'Quản trị viên' }]}>
            <Loading show={loadingGetDetail || deleteLoading} white />
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields })}
                actionBar={
                    <div className="action-bar">
                        <ElementWithPermission permissions={getCreatePermissions(PAGE_PATH)}>
                            <Button type="primary" onClick={onShowCreate}>
                                {/* i18n: 'Tạo {{var, lowercase}} mới', var = 'Quản trị viên' */}
                                <PlusOutlined /> Tạo quản trị viên mới
                            </Button>
                        </ElementWithPermission>
                    </div>
                }
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        rowKey={(record) => record.id}
                        pagination={pagination}
                    />
                }
            />
            <BasicModal
                visible={visible}
                isEditing={isEditing}
                objectName={OBJECT_NAME}
                loading={loadingSave}
                onOk={onOkModal}
                onCancel={() => setVisible(false)}
                formId="form-admin"
                bodyStyle={{
                    padding: '24px 24px',
                    maxHeight: 'calc(-40px + 78vh)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
            >
                <AdminForm
                    isEditing={isEditing}
                    dataDetail={isEditing ? dataDetail : {}}
                    loadingSave={loadingSave}
                    uploadFile={uploadFile}
                />
            </BasicModal>
        </PageWrapper>
    );
};

export default UserAdminListPage;
